import { Component, OnInit } from "@angular/core";
import { FormGroup, FormControl } from "@angular/forms";
import { Router, ActivatedRoute } from "@angular/router";
import { combineLatest } from "rxjs";
import {
  distinct,
  map,
  mergeAll,
  switchMap,
  tap,
  toArray,
} from "rxjs/operators";
import { PickupPoint } from "src/app/model/pickup-point";
import { AddressService } from "src/service/address.service";
import { DialogService } from "src/service/dialog.service";
import { WalletService } from "../../wallet/wallet.service";
import { CheckoutService } from "../checkout.service";

@Component({
  selector: "app-map",
  templateUrl: "./map.component.html",
  styleUrls: ["../../dashboard.style.scss"],
})
export class MapComponent implements OnInit {
  constructor(
    public addressService: AddressService,
    private checkoutService: CheckoutService,
    private router: Router,
    private route: ActivatedRoute,
    private walletService: WalletService,
    private dialogService: DialogService
  ) {}

  mapForm: FormGroup;
  pickupPointsResponse = this.addressService.getPickupPoints();
  pickupPoints;
  countries;
  cities;
  cameraLat;
  cameraLng;
  zoom = 8;
  loading = false;

  ngOnInit(): void {
    this.mapForm = new FormGroup({
      country: new FormControl(),
      city: new FormControl(),
      address: new FormControl(),
    });

    this.pickupPointsResponse = this.addressService.getPickupPoints();

    this.countries = this.addressService.getPickupPoints().pipe(
      mergeAll(),
      distinct((d: PickupPoint) => d.country),
      toArray(),
      map((item) => item.map((item) => item.country))
    );

    this.cities = this.mapForm.get("country").valueChanges.pipe(
      switchMap((value) =>
        this.pickupPointsResponse.pipe(
          mergeAll(),
          distinct((d: PickupPoint) => d.country),
          toArray(),
          map((item) =>
            item
              .filter((item) => item.country === value)
              .map((item: PickupPoint) => item.state)
          )
        )
      )
    );

    this.pickupPoints = combineLatest([
      this.mapForm.get("country").valueChanges,
      this.mapForm.get("city").valueChanges,
    ]).pipe(
      switchMap(([country, city]: any) =>
        this.pickupPointsResponse.pipe(
          map((response: [PickupPoint]) =>
            response.filter(
              (pickupPoint: PickupPoint) =>
                pickupPoint.country === country && pickupPoint.state === city
            )
          ),
          tap((pickupPoints: [PickupPoint]) => {
            this.cameraLat = pickupPoints[0]?.lat;
            this.cameraLng = pickupPoints[0]?.lon;
          })
        )
      )
    );
  }

  onPickPoint(event) {
    const id = event.value.id;
    this.addressService
      .getPickupPoints()
      .pipe(
        map((response: [PickupPoint]) =>
          response.find((item) => item.id === id)
        ),
        tap((pickupPoint) => {
          this.cameraLat = pickupPoint.lat;
          this.cameraLng = pickupPoint.lon;
          this.zoom = 15;
        })
      )
      .subscribe(
        () => {},
        (error) => this.dialogService.showErrorDialog(error)
      );
  }

  dontNavigateToPaymentTypes = ["vault", "savingPlanProduct"];

  onSubmit() {
    this.checkoutService.refreshCheckout({
      pickupLocId: this.mapForm.value.address.id,
      country: this.mapForm.value.address.country,
    });
    this.loading = true;
    const {
      type,
      routerLink,
      queryParams,
      request,
      afterSuccess,
      afterCloseDialog,
    } = this.checkoutService.selectedPaymentRequest.value;
    if (!this.dontNavigateToPaymentTypes.includes(type)) {
      this.router.navigate(["..", "payment"], { relativeTo: this.route });
    } else {
      request()
        .pipe(
          tap(() => (this.loading = false)),
          switchMap(() => afterSuccess()),
          switchMap(() => afterCloseDialog()),
          switchMap(() => this.walletService.syncWalletBalance())
        )
        .subscribe(
          () => {
            this.checkoutService.selectedShipment = null;
            this.router.navigate([routerLink], {
              relativeTo: this.route,
              queryParams,
            });
          },
          (error) => {
            this.checkoutService.selectedShipment = null;
            this.loading = false;
            this.dialogService.showErrorDialog(error.message);
          }
        );
    }
  }
}
